import { TESTER_CHANNEL_LABELS } from "@xop/dashboard-shared";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { EmptyState } from "../../components/ui/empty-state.js";
import { PageHeader } from "../../components/ui/page-header.js";
import { relativeTime } from "../../lib/relativeTime.js";
import { testerApi } from "./client.js";

export function TesterHistoryPage(): JSX.Element {
  const runsQuery = useQuery({
    queryKey: ["tester", "runs"],
    queryFn: testerApi.listRuns,
  });
  const runs = runsQuery.data?.runs ?? [];

  return (
    <Box>
      <PageHeader
        title="Run history"
        description="Every capability test that's been fired, newest first."
      />

      {runsQuery.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : runs.length === 0 ? (
        <EmptyState
          title="No tests run yet"
          description="Fire a test from the Run tab and it'll show up here."
        />
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {runs.map((r) => (
            <Card key={r.id} variant="outlined" sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={r.ok ? "OK" : "Failed"}
                      color={r.ok ? "success" : "error"}
                    />
                    <Typography variant="subtitle2">{r.target_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      → {r.target_phone}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ mt: 1, flexWrap: "wrap" }}
                  >
                    {r.channels.map((c) => (
                      <Chip
                        key={c}
                        size="small"
                        variant="outlined"
                        label={TESTER_CHANNEL_LABELS[c]}
                      />
                    ))}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {r.message}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {relativeTime(r.created_at)}
                  {r.created_by_email ? ` · ${r.created_by_email}` : ""}
                </Typography>
              </Box>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
